import { SelectorParam } from 'i18next';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { Priority } from '../../../types/enums';
import { LabeledRadioButton, ValidationErrorList } from '../Inputs';
import { Column, Row } from '../LayoutComponents';
import { PriorityFormState } from './PriorityForm.schema';

const testIds = {
  priorityButton: (testIdPrefix: string) =>
    `PriorityForm::${testIdPrefix}PriorityButton`,
};

type PriorityButtonProps = {
  readonly priority: Priority;
  readonly testIdPrefix: string;
  readonly translationKey: SelectorParam;
};

const defaultPriorities: ReadonlyArray<PriorityButtonProps> = [
  {
    priority: Priority.Standard,
    testIdPrefix: 'standard',
    translationKey: ($) => $.priority.standard,
  },
  {
    priority: Priority.Temporary,
    testIdPrefix: 'temporary',
    translationKey: ($) => $.priority.temporary,
  },
  {
    priority: Priority.Draft,
    testIdPrefix: 'draft',
    translationKey: ($) => $.priority.draft,
  },
];

type PriorityFormProps = {
  readonly hiddenPriorities?: ReadonlyArray<Priority>;
  readonly priorityButtonClassName?: string;
};

/**
 * Component for selecting priority.
 * Can be merged with other forms.
 */
export const PriorityForm: FC<PriorityFormProps> = ({
  hiddenPriorities,
  priorityButtonClassName,
}) => {
  const { t } = useTranslation();
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<PriorityFormState>();

  const selectedPriority = watch('priority');
  const setPriority = (value: Priority) =>
    setValue('priority', value, {
      shouldValidate: true,
      // Enable these, just like normal react form hook onChange would.
      shouldDirty: true, // Set dirty bit: value === defaultValue
      shouldTouch: true, // Set field touched bit: true
    });

  const displayedPriorities = defaultPriorities.filter(
    (priority) => !hiddenPriorities?.includes(priority.priority),
  );

  return (
    <Column>
      <fieldset>
        <legend className="font-bold">
          {t(($) => $.priority.label)}
          <span className="ml-1 text-dark-grey">*</span>
        </legend>
        <Row className="flex-wrap gap-2">
          {displayedPriorities.map(
            ({ priority, testIdPrefix, translationKey }) => (
              <LabeledRadioButton
                className={twJoin('grow', priorityButtonClassName)}
                id={`priority.${testIdPrefix}`}
                fieldPath="priority"
                value={priority}
                key={testIdPrefix}
                label={t(translationKey)}
                onClick={() => setPriority(priority)}
                selected={selectedPriority === priority}
                hasError={!!errors.priority}
                testId={testIds.priorityButton(testIdPrefix)}
              />
            ),
          )}
        </Row>
        <ValidationErrorList fieldPath="priority" />
      </fieldset>
    </Column>
  );
};
